/** @owner features/onboarding — registro de datos de usuario. Consumido por UserDataModal. */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { IRegisterUserDataPayload } from '../types'

interface UserDataState {
  userData: IRegisterUserDataPayload | null
  isLoading: boolean
  error: string | null
  
  // Actions
  setUserDataStart: () => void
  setUserDataSuccess: (userData: IRegisterUserDataPayload) => void
  setUserDataFailure: (error: string) => void
  clearUserData: () => void
  
  // Computed getters
  hasUserData: () => boolean
  isUserDataComplete: () => boolean
  isMedicalProfessionalDataComplete: () => boolean
}

export const useUserDataStore = create<UserDataState>()(
  persist(
    (set, get) => ({
      userData: null,
      isLoading: false,
      error: null,
      
      setUserDataStart: () => set({ 
        isLoading: true, 
        error: null 
      }),
      
      setUserDataSuccess: (userData) => set({ 
        userData, 
        isLoading: false, 
        error: null 
      }),
      
      setUserDataFailure: (error) => set({ 
        isLoading: false, 
        error 
      }),
      
      clearUserData: () => set({ 
        userData: null, 
        isLoading: false, 
        error: null 
      }),
      
      hasUserData: () => {
        const state = get()
        return state.userData !== null
      },
      
      isUserDataComplete: () => {
        const state = get()
        if (!state.userData) return false

        // Verificar que los campos requeridos estén completos
        const { name, surname } = state.userData
        return Boolean(name && surname)
      },

      // Nueva función específica para validar datos profesionales médicos
      isMedicalProfessionalDataComplete: () => {
        const state = get()
        if (!state.userData) return false

        const {
          name,
          surname,
          health_professional,
          id_profession,
          id_registration_type,
          registration_number,
          document_number
        } = state.userData



        return Boolean(
          name &&
          surname &&
          health_professional &&
          id_profession &&
          id_registration_type &&
          registration_number &&
          document_number
        )
      },
    }),
    {
      name: 'user-data-storage',
      partialize: (state) => ({
        userData: state.userData,
        // No persistir loading states ni errores
      }),
    }
  )
)

// Selectores útiles
export const useUserData = () => 
  useUserDataStore((state) => state.userData)

export const useUserDataLoading = () => 
  useUserDataStore((state) => state.isLoading)

export const useUserDataError = () =>
  useUserDataStore((state) => state.error)

export const useIsMedicalProfessionalDataComplete = () =>
  useUserDataStore((state) => state.isMedicalProfessionalDataComplete())

export const useUserDataStatus = () =>
  useUserDataStore((state) => ({
    userData: state.userData,
    isLoading: state.isLoading,
    error: state.error,
    hasUserData: state.hasUserData(),
    isUserDataComplete: state.isUserDataComplete(),
  }))

// Hook para manejar operaciones de userData
export const useUserDataActions = () => {
  const { 
    setUserDataStart, 
    setUserDataSuccess, 
    setUserDataFailure, 
    clearUserData 
  } = useUserDataStore()
  
  return {
    setUserDataStart,
    setUserDataSuccess,
    setUserDataFailure,
    clearUserData,
  }
}
